import { Injectable } from "@angular/core";
import { Observable, forkJoin, of } from "rxjs";
import { Config } from "src/app/config";
import { ClaimAssocText, ClaimCatDocDto, ClaimsViewInfo, ClaimsViewInfoDto, ClaimsViewModel, ClaimsViewModelDto, ClaimsViewStats, ClaimDocMetadata, ClaimDocMetadataDto, ClaimFootnote, ClaimReference, ClaimReferenceDto, ClaimFormModel, CreateClaimDto, EditClaimDto } from "../model/claims-view.model";
import { HttpClient } from "@angular/common/http";
import { AuthService } from "@shared/services/auth.service";
import { catchError, map } from "rxjs/operators";
import { AnnotationElement, ClaimReferenceAnchorResult, ServiceCallInfo } from "@shared/models/common";
import { CatDoc, ReferenceDocument } from "@shared/models/cat.model";
import { CatService } from "@shared/services/cat.service";
import { toCamelCaseProperties, toSnakeCaseProperties } from "@shared/utils";
import { ClaimReferenceDetails } from "../model/claim-reference-details";
import { EmbeddedObjectAnnotation } from "@shared/models/embedded-object-annotation";
import { IntegrationService } from "@shared/services/integration.service";


@Injectable()
export class ClaimsViewService {

    scaiServiceUrl = Config.assetConfigData['scaiServiceUrl'];
    
    constructor(private http: HttpClient, private authService: AuthService, private catService: CatService, private integrationService: IntegrationService) {
        
    }
    
    getClaims(catVersionId: number, pageNum: number, rowsPerPage: number, lang: string, filterText?: string, claimType?: string, status?: string): Observable<ClaimsViewInfo> {

        let url = this.scaiServiceUrl + `claims/${catVersionId}?page_num=${pageNum}&page_size=${rowsPerPage}&lang=${lang}`
        if (filterText) {
            url += `&filter_text=${encodeURIComponent(filterText)}`;
        }
        if (claimType) {
            url += `&claim_type=${encodeURIComponent(claimType)}`;
        }
        if (status) {
            url += `&status=${encodeURIComponent(status)}`;
        }
        return this.http
            .get<ClaimsViewInfoDto>(url, {
                headers: this.authService.getRequestHeaders(),
                withCredentials: false,
                responseType: 'json',
            })
            .pipe(
                map((data: ClaimsViewInfoDto) => {

                    const claimsStats: ClaimsViewStats = {
                        totalCount: data.totalCount
                    }
                    const claimsViewInfo: ClaimsViewInfo = {
                        claims: this.extractClaims(data.claims),
                        claimsStats: claimsStats
                    }
                    return claimsViewInfo
                    }
                ),
                catchError((error) => {
                    return this.authService.handleError(error, new ServiceCallInfo('get_claims_view', "", url));
                })
            );

    }
    saveClaimMetadata(claimId: number, metadataToSave: { [key: string]: string[] }) {
        
        let url = this.scaiServiceUrl + `claims-v2/${claimId}/metadata`
        return this.http
            .put(url, { "metadata": metadataToSave }, {
                headers: this.authService.getRequestHeaders(),
                withCredentials: false,
                responseType: 'json',
            }).pipe(catchError((error) => {
                    return this.authService.handleError(error, new ServiceCallInfo('update_claims_view', "", url));

            }))


    }
    setClaimStatus(claimId: number, status: string = 'APPROVED') {
        let url = this.scaiServiceUrl + `claims-v2/${claimId}/status`
        return this.http
            .post(url, { status: status }, {
                headers: this.authService.getRequestHeaders(),
                withCredentials: false,
                responseType: 'json',
            }).pipe(catchError((error) => {
                    return this.authService.handleError(error, new ServiceCallInfo('set_claim_status', "", url));
            }))
    }
    deleteClaim(claimId: number) {
        
        let url = this.scaiServiceUrl + `claims-v2/${claimId}`
        return this.http
            .delete(url, {
                headers: this.authService.getRequestHeaders(),
                withCredentials: false,
                responseType: 'json',
            }).pipe(catchError((error) => {
                    return this.authService.handleError(error, new ServiceCallInfo('delete_claim', "", url));

            }))

    }
    private extractClaims(data: ClaimsViewModelDto[]): ClaimsViewModel[] {

        return data.map((elem) => {

            return this.extractClaim(elem);
        })

    }
    private extractClaim(data: ClaimsViewModelDto): ClaimsViewModel {
        
        return {
            claimId: data.claim_id,
            catSubVersionId: data.cat_sub_version_id,
            catPhraseId: data.cat_phrase_id,
            mainText: data.main_text,
            phraseContentType: data.phrase_content_type,
            claimVersionId: data.claim_version_id,
            statement: data.statement,
            status: data.status,
            references: this.extractClaimReferences(data.references),
            footnotes: this.extractClaimFootnotes(data.footnotes),
            associatedText: this.extractAssociatedText(data.assoc_text),
            metadata: this.extractMetadata(data.metadata),
            docs: this.extractCatDocs(data.documents, data.claim_id || 0),
            isSelected: false
        }
    }


    private extractClaimReferences(data: ClaimReferenceDto[]): ClaimReference[] {

        if (!data) {
            return [];
        }
        return data.map((elem: ClaimReferenceDto) => {
            return  {
                refPhraseText: elem.ref_text,
                anchorPath: elem.anchor_paths ?? ""

            } as ClaimReference
        })

    }
    private extractClaimFootnotes(data: string[]): ClaimFootnote[] {
        if (!data) {
            return [];
        }
        return data.map((elem: string) => {
            return {footnoteText : elem} as ClaimFootnote
        })
    }
    private extractAssociatedText(data: string[]): ClaimAssocText[] {
        
        if (!data) {
            return [];
        }
        return data.map((elem: string) => {
            return {assocPhraseText : elem} as ClaimAssocText
        })
    }

    private extractMetadata(data: ClaimDocMetadataDto[]): ClaimDocMetadata[] {
        if (!data) {
            return [];
        }
        return data.map((elem: ClaimDocMetadataDto) => {
            return {fieldLabel : elem.field_label, fieldName: elem.field_name, fieldValues: this.normalizeFieldValues(elem.field_values)} as ClaimDocMetadata
        })
    }

    private normalizeFieldValues(field_values: unknown): string[] {
        if (!Array.isArray(field_values)) return [];
        const out: string[] = [];
        for (const it of field_values) {
            if (typeof it === 'string') out.push(it);
            else if (it && typeof it === 'object' && typeof (it as any).value_label === 'string') {
                out.push((it as any).value_label);
            }
        }
        return out;
    }

    private extractCatDocs(data: ClaimCatDocDto[], claimId: number): CatDoc[] {
        if (!data) {
            return [];
        }
        let camelCaseData = toCamelCaseProperties(data);
         
        return camelCaseData?.map(d => {
            let doc = new CatDoc();
            doc.docId = d.docId;
            doc.docName = d.docName;
            doc.versionId = d.versionId;
            doc.fileName = d.fileName;
            doc.docS3Key = d.docS3Key;
            doc.inddS3Key = d.inddS3Key;
            doc.pagePdfS3Key = d.pagePdfS3Key;
            doc.createDate = this.authService.formatDBDateTime(d.crDate);
            doc.singlePhrasePageUrl = d.phrasePageUrl;
            doc.singlePhraseEl = d.phraseEl;
            doc.singlePhrasePageNum = d.pageNum + 1;
            doc.pagesNum = d.pagesCnt;
            let ann = new AnnotationElement();
            ann.x1 = d.x1;
            ann.x2 = d.x2;
            ann.y1 = d.y1;
            ann.y2 = d.y2;
            this.catService._updPhraseAnnElementPolygon(ann, d.borderLines);
            ann.annColor = '#ff2229';
            ann.annId = claimId;
            doc.annotation = ann;
            return doc;
        })
    }
    setBatchClaimStatus(selectedClaims: ClaimsViewModel[], status: string = 'APPROVED'): Observable<any> {
        /*POST /claims-v2/batch-status
                {
                    "ids": [1, 2, 3],
                    "status": "APPROVED"
                }
            */
        let url = this.scaiServiceUrl + `claims-v2/batch-status`;
        return this.http.post(url, { "ids": selectedClaims.map(c => c.claimId), "status": status }, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json',
        }).pipe(catchError((error) => {
                return this.authService.handleError(error, new ServiceCallInfo('batch_set_claim_status', "", url));
        }));
    }

    deleteClaims(selectedClaims: ClaimsViewModel[]): Observable<any> { 
        

        return this.http.post(this.scaiServiceUrl + `claims-v2/batch-delete`, { "ids": selectedClaims.map(c => c.claimId) }, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json',
        }).pipe(catchError((error) => {
                return this.authService.handleError(error, new ServiceCallInfo('delete_claims', "", this.scaiServiceUrl + `claims-v2/batch-delete`));
        }));

    }
    // ref docs loaded for resolving ref doc names are returned too, so claim form doesn't have to load them again
    getClaimDetails(claimId: number, lang: string, catVersionId: number): Observable<{ claim: ClaimFormModel, refDocs: ReferenceDocument[] }> {

        let url = this.scaiServiceUrl + `claims-v2/${claimId}?lang=${lang}`;
        const claim$ = this.http.get(url, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json'
            }
        );
        // ref docs are used to resolve ref doc name of implicit references - claim can be displayed without them
        const refDocs$ = this.integrationService.getAvailableReferenceDocs(catVersionId).pipe(catchError(() => of([] as ReferenceDocument[])));

        return forkJoin([claim$, refDocs$]).pipe(map(([data, refDocs]: [any, ReferenceDocument[]]) => {

        let footnotes = data.footnotes?.map((footnote) => ({
            footnoteText: footnote
        } as ClaimFootnote)) || [];
        let associatedText = data.associated_text?.map((assocText) => ({
            assocPhraseText: assocText
        } as ClaimAssocText )) || [];
        let references = data.references?.map((reference) => ({
            refDocId: reference.ref_doc_id,
            // implicit references (without ref text) display ref doc name
            refPhraseText: reference.ref_text || this.resolveRefDocName(reference.ref_doc_id, refDocs),
            // references without ref text are implicit
            referenceType: reference.ref_text?.trim() ? 'EXPLICIT' : 'IMPLICIT',
            anchors: reference.ref_doc_anchors?.map((anchor) => {
                
                let embeddedObjectAnnotation: EmbeddedObjectAnnotation = this.catService.mapToEmbeddedObjectAnnotation(anchor.embeddedObjectAnnotation);                
                if (embeddedObjectAnnotation == null) {
                    embeddedObjectAnnotation = new EmbeddedObjectAnnotation();
                }
                embeddedObjectAnnotation.anchorPath = anchor.anchor_path;
                return new ClaimReferenceAnchorResult(anchor.ref_doc_anchor_id, anchor.ref_doc_anchor_id, anchor.ref_doc_id, embeddedObjectAnnotation);
            }) ?? [],
            alreadyExists: true
        } as ClaimReferenceDetails ));
        // explicit references are displayed first, then implicit (sort is stable, so API order is kept within each type)
        references?.sort((a, b) => (a.referenceType == 'IMPLICIT' ? 1 : 0) - (b.referenceType == 'IMPLICIT' ? 1 : 0));
        let metadataDetails = this.extractMetadata(data.metadata);

        let claimFormModel: ClaimFormModel = {
                claimText: data.main_text,
                contentType: data.claim_type,
                status: data.status,
                footnotes: footnotes,
                associatedText: associatedText,
                references: references,
                metadata: metadataDetails, 
                catVersionId: catVersionId,
                claimId: data.claim_id

            }
            return { claim: claimFormModel, refDocs: refDocs }
        }),
        catchError((error) => {
                
            return this.authService.handleError(error, new ServiceCallInfo('get_claim_details', "", url));

        }))

    }

    // fallback for references without ref_text - take docName of the reference document
    private resolveRefDocName(refDocId: number | null | undefined, refDocs: ReferenceDocument[]): string {

        return refDocs.find((refDoc) => refDoc.docId == refDocId)?.docName ?? "";
    }
  saveClaimChanges(claimsViewModel: ClaimFormModel, metadata: { [key: string]: string[]; } | null) {
  
    const url = `${this.scaiServiceUrl}claims-v2`;
    const createClaimDto: CreateClaimDto = this.initializeCreateClaimDto(claimsViewModel, metadata);

      return this.http
      .post<any[]>(url, toSnakeCaseProperties(createClaimDto), {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {

            return response
        }),
      catchError((error) => this.authService.handleError(error, new ServiceCallInfo('save-claims-change', url, "")))
      )

  }
  private getClaimProperties(claimsViewModel: ClaimFormModel) {
    
    let mainText = claimsViewModel.claimText;

    let associatedText = claimsViewModel.contentType == 'TEXT_CONTENT' ? [] : claimsViewModel.associatedText?.filter((assocText) => assocText && assocText.assocPhraseText != "")?.map((assocText) => assocText.assocPhraseText) || [];
    let footnotes = claimsViewModel.contentType == 'TEXT_CONTENT' ? [] : claimsViewModel.footnotes?.filter((footnote) => footnote && footnote.footnoteText !== "")?.map((footnote) => footnote.footnoteText) || [];
    let status = claimsViewModel.status;
    let phraseContentType = claimsViewModel.contentType;
    let references = claimsViewModel.contentType == 'TEXT_CONTENT' ? [] : claimsViewModel.references?.filter((reference) => reference.referenceType == 'IMPLICIT' || (reference.refPhraseText && reference.refPhraseText !== ""))?.map((ref) => ({
      // ref doc matched via reference mappings is not set on the reference, so it is taken from added anchors
      refDocId: ref.refDocId ?? ref.anchors?.find((anchor) => anchor?.refDocId)?.refDocId ?? null,
      // ref citation text is not sent for implicit references
      refCitation: ref.referenceType == 'IMPLICIT' ? null : ref.refPhraseText,
      refDocAnchorsIds: (ref.anchors?.filter((anchor) => anchor && anchor.refDocAnchorId !== null)?.map((anchor) => anchor.refDocAnchorId) ?? [] )as number[]
    })) || [];
    let catVersionId = claimsViewModel.catVersionId;

    return {
        mainText: mainText,
        associatedText: associatedText,
        footnotes: footnotes,
        status: status,
        phraseContentType: phraseContentType,
        references: references,
        catVersionId: catVersionId
    }

  }
  private initializeCreateClaimDto(claimsViewModel: ClaimFormModel, metadata: { [key: string]: string[]; } | null): CreateClaimDto {

    const claimProperties = this.getClaimProperties(claimsViewModel);

    let createClaimDto: CreateClaimDto = {
      
      mainText: claimProperties.mainText,
      associatedText: claimProperties.associatedText,
      footnotes: claimProperties.footnotes,
      status: claimProperties.status,
      metadata: metadata,
      references: claimProperties.references,
      claimType: claimProperties.phraseContentType,
      catVersionId: claimProperties.catVersionId

    }
    return createClaimDto;
  }
    editClaimChanges(claimFormModel: ClaimFormModel, metadata: { [key: string]: string[]; } | null) {

        const url = `${this.scaiServiceUrl}claims-v2/${claimFormModel.claimId}`;
        const editClaimDto: EditClaimDto = this.initializeEditClaimDto(claimFormModel, metadata);
      return this.http
      .put<any[]>(url, toSnakeCaseProperties(editClaimDto), {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {

            return response
        }),
      catchError((error) => this.authService.handleError(error, new ServiceCallInfo('edit-claims-change', url, "")))
      )
    }

    saveBulkClaimChanges(claimsIds: number[], metadata: { [key: string]: string[]; } | null) {
        const url = `${this.scaiServiceUrl}claims-v2/metadata`;
        let payload = {
            ids: claimsIds,
            metadata: metadata
        }
        return this.http.patch<any[]>(url, payload, {
            headers: this.authService.getRequestHeaders(),
            withCredentials: false,
            responseType: 'json'
        }).pipe(
        map((response) => {

            return response
        }),
      catchError((error) => this.authService.handleError(error, new ServiceCallInfo('bulk-claim-metadata-change', url, "")))
      )

    }
    private initializeEditClaimDto(claimFormModel: ClaimFormModel, metadata: { [key: string]: string[]; } | null): EditClaimDto {
        const claimProperties = this.getClaimProperties(claimFormModel);
        let editClaimDto: EditClaimDto = {
        
        mainText: claimProperties.mainText,
        associatedText: claimProperties.associatedText,
        footnotes: claimProperties.footnotes,
        metadata: metadata,
        references: claimProperties.references,
        claimType: claimProperties.phraseContentType,

        }
        return editClaimDto;
    }
}